import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceAgreementQuestionCommentFragment,
  PerformanceAgreementQuestionnaireSectionFragment,
  PerformanceAgreementQuestionnaireSubjectFragment,
  PerformanceAgreementQuestionnairesFragment,
  PerformanceElementFragment,
} from 'generated/resources/graphql'
import { Dictionary, keyBy, zip } from 'lodash'

import ColumnByActiveActor from './ColumnByActiveActor'
import { AgreementQuestionnaireAnswer } from './_AgreementQuestionnaireContent'

type QuestionnaireContent = PerformanceAgreementQuestionnaireSectionFragment['content']
interface Props {
  managerContent: QuestionnaireContent
  selfContent: QuestionnaireContent
  agreement: PerformanceAgreementQuestionnairesFragment
  reviewer?: PerformanceAgreementQuestionnaireSubjectFragment
  target?: PerformanceAgreementQuestionnaireSubjectFragment
  hasManagerData: boolean
  hasTargetData: boolean
}

interface NestionQuestionProp {
  elementTuple: PerformanceElementTuples
  commentsByQuestions: CommentsByQuestions
  agreement: PerformanceAgreementQuestionnairesFragment
  reviewer?: PerformanceAgreementQuestionnaireSubjectFragment
  target?: PerformanceAgreementQuestionnaireSubjectFragment
  hasManagerData: boolean
  hasTargetData: boolean
}

const QuestionSectionPairRow = ({
  elementTuple,
  commentsByQuestions,
  agreement,
  reviewer,
  target,
  hasTargetData,
  hasManagerData,
}: NestionQuestionProp) => {
  const [manageeSection, managerSection] = elementTuple

  const manageeQuestionBySection = manageeSection?.questions || []
  const managerQuestionBySection = managerSection?.questions || []

  const combinedList = zip(managerQuestionBySection, manageeQuestionBySection)

  const managerSectionTitle = managerSection?.sectionTitle
  const manageeSectionTitle = manageeSection?.sectionTitle

  const hasSectionTitle = managerSectionTitle || manageeSectionTitle

  return (
    <>
      {hasSectionTitle && (
        <Box gap="s24" flexDirection="row">
          {managerSectionTitle && (
            <Box width="full">
              <Text size="400" color="black">
                {managerSectionTitle}
              </Text>
            </Box>
          )}
          {manageeSectionTitle && (
            <Box width="full">
              <Text size="400" color="black">
                {manageeSectionTitle}
              </Text>
            </Box>
          )}
        </Box>
      )}
      {combinedList.map(([q1, q2], index) => (
        <Box
          flexDirection="row"
          paddingTop={!hasSectionTitle && index === 0 ? 's0' : 's12'}
          gap="s24"
          key={index}
        >
          <ColumnByActiveActor
            hasTargetData={hasTargetData}
            hasManagerData={hasManagerData}
            managee={
              q1 && (
                <>
                  <Text size="300" color="black">
                    {q1.question.title}
                  </Text>
                  <AgreementQuestionnaireAnswer
                    key={index}
                    answeredQuestion={q1}
                    subject={target || undefined}
                    comment={commentsByQuestions[q1.question.uuid]}
                    canComment={!!(agreement._policies?.addNotesAsTarget.can && !agreement.locked)}
                    agreementId={agreement.id}
                    role={'target'}
                  />
                </>
              )
            }
            manager={
              q2 && (
                <>
                  <Text size="300" color="black">
                    {q2.question.title}
                  </Text>
                  <AgreementQuestionnaireAnswer
                    key={index}
                    answeredQuestion={q2}
                    subject={reviewer || undefined}
                    comment={commentsByQuestions[q2.question.uuid]}
                    canComment={!!(agreement._policies?.addNotesAsManager.can && !agreement.locked)}
                    agreementId={agreement.id}
                    role={'manager'}
                  />
                </>
              )
            }
          />
        </Box>
      ))}
    </>
  )
}

type PerformanceElementTuples = [
  PerformanceElementFragment | undefined,
  PerformanceElementFragment | undefined,
]

type CommentsByQuestions = Dictionary<PerformanceAgreementQuestionCommentFragment>

const getAlignedElements = (
  managerContent: QuestionnaireContent,
  selfContent: QuestionnaireContent
): PerformanceElementTuples[] => {
  const pairs = zip(managerContent, selfContent).map(([manager, self]) => ({
    manager,
    self,
    isMatched: manager?.type === self?.type,
  }))

  const sectionPairs: Array<PerformanceElementTuples> = []
  const orphanedQuestions: Record<'manager' | 'self', PerformanceElementFragment[]> = {
    manager: [],
    self: [],
  }

  pairs.forEach(({ manager, self, isMatched }) => {
    if (isMatched) {
      sectionPairs.push([manager, self])
    } else {
      const managerIsSection = manager?.type === 'answered_section'
      const selfIsSection = self?.type === 'answered_section'

      if (managerIsSection || selfIsSection) {
        sectionPairs.push([
          managerIsSection ? manager : undefined,
          selfIsSection ? self : undefined,
        ])

        if (manager?.type === 'answered_question') orphanedQuestions.manager.push(manager)
        if (self?.type === 'answered_question') orphanedQuestions.self.push(self)
      } else {
        if (manager) orphanedQuestions.manager.push(manager)
        if (self) orphanedQuestions.self.push(self)
      }
    }
  })

  const orphanedPairs = zip(orphanedQuestions.manager, orphanedQuestions.self)
  return [...sectionPairs, ...orphanedPairs]
}

const AlignedSectionableQuestions = ({
  managerContent,
  selfContent,
  agreement,
  reviewer,
  target,
  hasTargetData,
  hasManagerData,
}: Props) => {
  const commentByQuestionUuid: CommentsByQuestions = keyBy(
    [...agreement.managerComments, ...agreement.selfComments],
    (c) => c.questionUuid
  )
  const alignedElements = getAlignedElements(managerContent, selfContent)

  return (
    <>
      {alignedElements.map((elementTuple, index) => (
        <Box key={index} paddingBottom={index === alignedElements.length - 1 ? 's0' : 's12'}>
          <QuestionSectionPairRow
            elementTuple={elementTuple}
            commentsByQuestions={commentByQuestionUuid}
            agreement={agreement}
            reviewer={reviewer || undefined}
            target={target || undefined}
            hasManagerData={hasManagerData}
            hasTargetData={hasTargetData}
          />
        </Box>
      ))}
    </>
  )
}

export default AlignedSectionableQuestions
